import {
  type Territory,
  useConfig,
} from "$global-providers/config-provider/provider";
import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import type { Patient } from "$global-providers/rxdb-provider/collections/patient";
import type { UserContextUser } from "$global-providers/user-provider";
import {
  COUNTRY_CODES,
  NEXT_OF_KIN_RELATIONSHIP_CODES,
  TERRITORY,
} from "$routes/_lib/constants";
import { randomId } from "$routes/_lib/random-id";
import {
  mapContactPointOwnerToPhoneOwnership,
  PhoneOwnershipRadioOptions,
  phoneOwnershipToContactPointOwnerMap,
} from "$routes/new/patient/_lib/sections/phone-ownership";
import {
  addressLine,
  dateOfBirthValidation,
  phoneNumberValidationMessage,
  validatePhoneNumber,
} from "$routes/new/patient/_lib/sharedFormValidation";
import { getNationalIdValidationSchema } from "$routes/new/patient/step2/_lib/national-id-schema";
import {
  getGuardianValidationSchema,
  kenyaEducationValidationSchema,
} from "$routes/new/patient/step2/page";
import { INSURANCE_PROVIDERS } from "$routes/new/patient/step3/_lib/insurance/insurance-providers/insurance-providers";
import type { InsuranceProvider } from "$routes/new/patient/step3/_lib/insurance/types";
import { getActiveGuardian } from "$routes/patient/_lib/get-active-guardian";
import { mapGuardianToPatientGuardian } from "$routes/patient/_lib/guardian-mapper";
import { isChildPatient } from "$routes/patient/[:patientId]/_lib/is-child-patient";
import {
  InsuranceFormSchema,
  insuranceProviderToValidator,
} from "@elephant-healthcare/utils-insurance";
import { format } from "date-fns";
import {
  type ContactPointOwnership,
  MaritalStatus,
  NationalIdOwner,
  PatientHasInsurance,
} from "elephant-web/gql";
import { useMemo } from "react";
import type { RxDocument } from "rxdb";
import {
  type AnyObjectSchema,
  type AnySchema,
  type InferType,
  mixed,
  object,
  string,
} from "yup";

type InsuranceProviderValidator = {
  providerValidator: AnyObjectSchema;
  visitValidator: AnyObjectSchema;
};

function getValidatorForInsuranceProvider(
  insuranceProvider: InsuranceProvider,
): InsuranceProviderValidator {
  const providerValidator = insuranceProviderToValidator(
    InsuranceFormSchema.REGISTRATION,
    insuranceProvider,
  );

  const visitValidator = insuranceProviderToValidator(
    InsuranceFormSchema.VISIT,
    insuranceProvider,
  );

  return { providerValidator, visitValidator };
}

const useInsuranceValidation = (insuranceProviders: InsuranceProvider[]) =>
  useMemo(() => {
    const insuranceProviderValidators = insuranceProviders.reduce(
      (map: Map<string, InsuranceProviderValidator>, ip: InsuranceProvider) =>
        map.set(ip.insuranceType, getValidatorForInsuranceProvider(ip)),
      new Map<string, InsuranceProviderValidator>(),
    );

    // When the insuranceType matches an insurance provider,
    // AND `hasInsurance` is true,
    // set the validation of the `insuranceDetails` object to
    // the correct validator as per the JSON schema
    //
    // Check InsuranceInfoInput where we map parent to
    // .insuranceDetails so that validations are applied correctly
    return object({
      hasInsurance: string()
        .oneOf([
          PatientHasInsurance.Yes,
          PatientHasInsurance.No,
          PatientHasInsurance.Unknown,
        ])
        .required(),
      type: string().when(
        "hasInsurance",
        (hasInsurance: string, schema: AnySchema) =>
          hasInsurance === PatientHasInsurance.Yes ? schema.required() : schema,
      ),
      details: object().when(
        ["type", "hasInsurance"],
        // @ts-expect-error Legacy reason unknown
        (type: string, hasInsurance: string, schema) => {
          if (hasInsurance !== PatientHasInsurance.Yes) return schema;
          return (
            insuranceProviderValidators.get(type)?.providerValidator ?? schema
          );
        },
      ),
    });
  }, [insuranceProviders]);

export type FormValues = InferType<ReturnType<typeof useValidationSchema>>;

export const useValidationSchema = (patient?: RxDocument<Patient>) => {
  const { territory } = useConfig();
  const [registrationReformatFlag] = useFeatureFlag(
    featureFlags.immunisations.registrationReformat,
  );
  const insuranceValidationSchema = useInsuranceValidation(
    INSURANCE_PROVIDERS[territory],
  );

  const isChild = patient ? isChildPatient(new Date(patient.dob)) : false;

  return object({
    name: object({
      first: string().required(),
      middle: territory === TERRITORY.KENYA ? string().required() : string(),
      last: string().required(),
    }).required(),
    dob: dateOfBirthValidation().required(),
    gender: string().oneOf(["m", "f", "transgender"]).required(),
    phone: object({
      ownership: string()
        .oneOf(Object.values(PhoneOwnershipRadioOptions))
        .required(),
      number: mixed().when("ownership", {
        is: (phoneOwnership: ContactPointOwnership) =>
          phoneOwnership &&
          (phoneOwnership.includes(PhoneOwnershipRadioOptions.Personal) ||
            phoneOwnership.includes(PhoneOwnershipRadioOptions.Shared)),
        then: string().test(
          "phone number validation",
          phoneNumberValidationMessage(territory),
          (phoneNumber) => validatePhoneNumber(phoneNumber, territory),
        ),
        otherwise: string().nullable(),
      }),
    }),
    address: object({
      line1: addressLine().required(),
      line2: addressLine().required(),
      line3:
        territory === TERRITORY.NIGERIA
          ? addressLine().required()
          : addressLine(),
      line4: addressLine().required(),
    }).required(),
    email: string().email().nullable(),
    language: string().optional(),
    maritalStatus: string()
      .oneOf(Object.values(MaritalStatus))
      .when("dob", {
        is: (dob: Date) => isChildPatient(dob),
        then: string().optional(),
        otherwise: string().required(),
      }),
    nationalId: mixed().when("dob", {
      is: (dob: Date) => isChildPatient(dob),
      then: object().optional(),
      otherwise: getNationalIdValidationSchema(territory),
    }),
    nationality: string().oneOf(Object.values(COUNTRY_CODES)).required(),
    nextOfKin: object({
      fullName: string().notRequired(),
      phoneNumber: string().when("fullName", {
        is: (fullName) => !!fullName,
        then: string()
          .test(
            "phone number validation",
            phoneNumberValidationMessage(territory),
            (phoneNumber) => validatePhoneNumber(phoneNumber, territory),
          )
          .required(),
      }),
      relationship: string().when("fullName", {
        is: (fullName) => !!fullName,
        then: string()
          .oneOf(Object.values(NEXT_OF_KIN_RELATIONSHIP_CODES))
          .required(),
      }),
      address: object({
        line1: addressLine(),
        line2: addressLine(),
        line3: addressLine(),
        line4: addressLine(),
      }),
    }),
    insurance: insuranceValidationSchema.required(),
    ...(registrationReformatFlag
      ? getGuardianValidationSchema(territory, isChild)
      : {}),
    ...(territory === TERRITORY.KENYA ? kenyaEducationValidationSchema : {}),
  });
};

export const formMaritalStatusToRxDbValueMap = {
  [MaritalStatus.Unk]: "unknown",
  [MaritalStatus.L]: "divorced",
  [MaritalStatus.M]: "married",
  [MaritalStatus.U]: "single",
  [MaritalStatus.W]: "widowed",
};

export const rxDbMaritalStatusToFormValueMap = {
  unknown: MaritalStatus.Unk,
  divorced: MaritalStatus.L,
  married: MaritalStatus.M,
  single: MaritalStatus.U,
  widowed: MaritalStatus.W,
};

export const getInitialValues = (
  patient: RxDocument<Patient>,
  handleChildren?: boolean,
): FormValues => {
  const activeGuardian = getActiveGuardian(patient);

  const isChild = isChildPatient(new Date(patient.dob)) && handleChildren;
  return {
    name: {
      first: patient.name.first,
      middle: patient.name.middle ?? "",
      last: patient.name.last,
    },
    dob: new Date(patient.dob),
    gender: patient.gender,
    nationalId: {
      number: patient.nationalId?.value ?? "",
      type: undefined,
      owner: patient.nationalId?.owner ?? undefined,
    },
    address: {
      line1: patient.address.line1,
      line2: patient.address.line2,
      line3: patient.address.line3,
      line4: patient.address.line4,
    },
    maritalStatus:
      patient.maritalStatus && !isChild
        ? rxDbMaritalStatusToFormValueMap[patient.maritalStatus]
        : null,
    insurance: {
      hasInsurance:
        patient.insurance?.type === PatientHasInsurance.Unknown
          ? PatientHasInsurance.Unknown
          : patient?.insurance
            ? PatientHasInsurance.Yes
            : PatientHasInsurance.No,
      type: patient.insurance?.type,
      details: patient.insurance?.details as any,
    },
    education:
      patient.education && !isChild
        ? {
            isStudent: patient.education.isStudent,
            nemisNumber: patient.education.nemisNumber,
            school: patient.education.school,
          }
        : null,
    email: patient.email ? patient.email : null,
    phone:
      patient.phone && !isChild
        ? {
            number: patient.phone.number,
            ownership: mapContactPointOwnerToPhoneOwnership(
              patient.phone.ownership as ContactPointOwnership,
            ),
          }
        : {
            number: "",
            ownership: PhoneOwnershipRadioOptions.None,
          },
    nextOfKin:
      patient.nextOfKin && !isChild
        ? {
            address: patient.nextOfKin.address
              ? patient.nextOfKin.address
              : { line1: "", line2: "", line3: "", line4: "" },
            fullName: patient.nextOfKin.fullName,
            phoneNumber: patient.nextOfKin.phoneNumber,
            relationship: patient.nextOfKin.relationship,
          }
        : {
            fullName: "",
            phoneNumber: "",
            relationship: "",
            address: {
              line1: "",
              line2: "",
              line3: "",
              line4: "",
            },
          },
    nationality: patient.nationality,
    language: "",
    guardian: activeGuardian
      ? {
          fullName: activeGuardian.fullName,
          phone: activeGuardian.phone
            ? {
                ownership: mapContactPointOwnerToPhoneOwnership(
                  activeGuardian.phone.ownership,
                ),
                number: activeGuardian.phone.number,
              }
            : {
                number: "",
                ownership: PhoneOwnershipRadioOptions.None,
              },
          relationship: activeGuardian.relationship,
          consents: {
            healthcareComms: null,
            marketingComms: null,
          },
          patientId: activeGuardian.patientId || null,
        }
      : null,
    // TODO: Add language to the patient object
    // language: patient.language ?? null,
  };
};

// Convert dates to ISO strings so that RxDB doesn't save an
// object.
export const serialisePatientInsuranceDetails = (details: unknown) =>
  Object.entries(details).reduce((acc, [key, value]) => {
    if (value instanceof Date) {
      acc[key] = value.toISOString();
    } else {
      acc[key] = value;
    }
    return acc;
  }, {});

export const mapGuardians = ({
  formGuardian,
  existingGuardians,
  getCurrentTime,
  userId,
}: {
  formGuardian: FormValues["guardian"];
  existingGuardians: Patient["guardians"];
  getCurrentTime: () => Date;
  userId: string;
}): Patient["guardians"] => {
  if (!formGuardian) return null;

  const currentTime = getCurrentTime().toISOString();

  const createNewGuardian = (): Patient["guardians"][number] => {
    const newGuardianArray = mapGuardianToPatientGuardian({
      guardianData: formGuardian,
      user: { id: userId },
      currentTime,
      generateId: randomId,
    });
    return newGuardianArray?.[0] || null;
  };

  // If there are no existing guardians, create a new one
  if (!existingGuardians || existingGuardians.length === 0) {
    const newGuardian = createNewGuardian();
    return newGuardian ? [newGuardian] : null;
  }

  // Find active guardian (where meta.deleted is false)
  const activeGuardian = existingGuardians.find(
    (guardian) => guardian.meta && !guardian.meta.deleted,
  );

  // A guardian is matching if the full name and relationship are the same.
  // Guardians can change their phone number.
  const isMatchingGuardian =
    activeGuardian &&
    activeGuardian.fullName === formGuardian.fullName &&
    activeGuardian.relationship === formGuardian.relationship;

  if (isMatchingGuardian) {
    const formPhoneOwnership =
      phoneOwnershipToContactPointOwnerMap[formGuardian.phone?.ownership];

    if (
      activeGuardian.phone?.number === formGuardian.phone?.number &&
      activeGuardian.phone?.ownership === formPhoneOwnership
    ) {
      return existingGuardians;
    }

    return existingGuardians.map((guardian) => {
      if (guardian.id === activeGuardian.id) {
        const newGuardian = createNewGuardian();
        return {
          ...guardian,
          phone: newGuardian?.phone || null,
          meta: {
            ...guardian.meta,
            updatedAt: currentTime,
            updatedByUserId: userId,
          },
        };
      }
      return guardian;
    });
  }

  // Mark existing guardians as deleted and add new guardian
  const newGuardian = createNewGuardian();
  return [
    ...existingGuardians.map((guardian) => ({
      ...guardian,
      meta: {
        ...guardian.meta,
        deleted: true,
        updatedAt: currentTime,
        updatedByUserId: userId,
      },
    })),
    ...(newGuardian ? [newGuardian] : []),
  ];
};

export const convertFormValuesToRxPatient = ({
  formValues,
  patient,
  user,
  getCurrentTime,
  territory,
}: {
  formValues: FormValues;
  patient: Patient;
  user: UserContextUser;
  getCurrentTime: () => Date;
  territory: Territory;
}): Patient => {
  return {
    id: patient.id,
    address: {
      country: patient.address.country,
      line1: formValues.address.line1,
      line2: formValues.address.line2,
      line3: formValues.address.line3,
      line4: formValues.address.line4,
    },
    dob: format(formValues.dob, "yyyy-MM-dd"),
    education: formValues.education
      ? {
          isStudent: formValues.education.isStudent,
          nemisNumber: formValues.education.nemisNumber ?? null,
          school: formValues.education.school ?? null,
        }
      : null,
    email: formValues.email ? formValues.email : null,
    gender: formValues.gender as "m" | "f" | "transgender",
    insurance:
      formValues.insurance?.hasInsurance === PatientHasInsurance.Yes
        ? {
            type: formValues.insurance?.type,
            number:
              formValues.insurance?.details?.insuranceNumber ??
              // TODO: Remove this. This is because ASHIA insurance was
              // incorrectly added with a misnamed insuranceNumber field.
              formValues.insurance?.details?.registrationNumber,
            details: formValues.insurance?.details
              ? serialisePatientInsuranceDetails(formValues.insurance.details)
              : null,
          }
        : null,
    maritalStatus: formMaritalStatusToRxDbValueMap[formValues.maritalStatus],
    meta: {
      createdAt: patient.meta.createdAt,
      createdByUserId: patient.meta.createdByUserId,
      updatedAt: getCurrentTime().toISOString(),
      updatedByUserId: user.id,
    },
    name: {
      first: formValues.name.first,
      middle: formValues.name.middle ?? null,
      last: formValues.name.last,
    },
    nationalId: formValues.nationalId.number
      ? {
          value: formValues.nationalId.number,
          owner: patient.nationalId?.owner ?? NationalIdOwner.Self,
        }
      : null,
    nationality: formValues.nationality,
    // TODO: Add language to the patient object
    // language: formValues.language,
    nextOfKin: formValues.nextOfKin.fullName
      ? {
          address: formValues.nextOfKin.address
            ? {
                country:
                  territory === TERRITORY.KENYA
                    ? COUNTRY_CODES.KENYA
                    : COUNTRY_CODES.NIGERIA,
                line1: formValues.nextOfKin.address.line1,
                line2: formValues.nextOfKin.address.line2,
                line3: formValues.nextOfKin.address.line3,
                line4: formValues.nextOfKin.address.line4,
              }
            : null,
          fullName: formValues.nextOfKin.fullName,
          phoneNumber: formValues.nextOfKin.phoneNumber,
          relationship: formValues.nextOfKin
            .relationship as Patient["nextOfKin"]["relationship"],
        }
      : null,
    phone:
      formValues.phone.ownership === "None"
        ? null
        : {
            number: formValues.phone.number,
            ownership:
              formValues.phone.ownership.toLowerCase() as ContactPointOwnership,
          },
    guardians: mapGuardians({
      formGuardian: formValues.guardian,
      existingGuardians: patient.guardians,
      getCurrentTime,
      userId: user.id,
    }),
    // TODO: If we could use incremental patch on the patient object, we could
    // avoid setting these fields.
    consent: patient.consent,
    facilityId: patient.facilityId,
    newToFacility: patient.newToFacility,
    organisationId: patient.organisationId,
    registrationFee: patient.registrationFee,
    // --- up til here ---
  };
};
